import React, { useState, useEffect, useMemo } from "react";
import NavbarPag from "../components/NavbarPag";

interface Client {
  rut: string;
  name: string;
}

interface UploadedDocument {
  id: string;
  rut: string;
  fileName: string;
  uploadDate: any;
  fileUrl: string;
  storagePath?: string;
}

const apiBase =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://api-kdps2abp6q-uc.a.run.app";

const formatDate = (uploadDate: any) => {
  if (!uploadDate) return "—";
  const d = new Date(uploadDate);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-CL");
};

const AdminUploadDocuments = () => {
  const [rut, setRut] = useState("");
  const [name, setName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [clientsError, setClientsError] = useState(false);
  const [filterRut, setFilterRut] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [deletingDoc, setDeletingDoc] = useState<UploadedDocument | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const itemsPerPage = 10;

  const clientMap = useMemo(() => {
    const map: Record<string, string> = {};
    clients.forEach((c) => { map[c.rut] = c.name; });
    return map;
  }, [clients]);

  const fetchClients = async () => {
    setClientsError(false);
    try {
      const res = await fetch(`${apiBase}/getClients`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientsError(true);
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${apiBase}/getAllDocuments`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchDocuments();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rut || files.length === 0) {
      setMessage("Faltan datos. Selecciona RUT y al menos un archivo.");
      return;
    }
    setIsUploading(true);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      setMessage(`Subiendo archivo ${i + 1} de ${files.length}...`);
      try {
        const formData = new FormData();
        formData.append("rut", rut);
        formData.append("file", files[i]);
        const uploadRes = await fetch(`${apiBase}/uploadDocument`, {
          method: "POST",
          body: formData,
        });
        const uploadJson = await uploadRes.json();
        if (!uploadRes.ok || !uploadJson.success) {
          throw new Error(uploadJson.error || `HTTP ${uploadRes.status}`);
        }
        successCount++;
      } catch (error: any) {
        console.error(`Error subiendo ${files[i].name}:`, error);
        errorCount++;
      }
    }

    await fetchDocuments();
    setCurrentPage(1);

    if (errorCount === 0) {
      setMessage(`✓ ${successCount} documento${successCount !== 1 ? "s" : ""} subido${successCount !== 1 ? "s" : ""} correctamente`);
    } else {
      setMessage(`✓ ${successCount} subido${successCount !== 1 ? "s" : ""}, ✗ ${errorCount} con error`);
    }

    setTimeout(() => {
      setRut("");
      setName("");
      setFiles([]);
      setMessage("");
      setIsUploadModalOpen(false);
    }, 1500);

    setIsUploading(false);
  };

  const handleDownload = (docId: string) => {
    window.open(`${apiBase}/downloadDocument?id=${docId}`, "_blank");
  };

  const handleDeleteDoc = async () => {
    if (!deletingDoc) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${apiBase}/deleteDocument`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: deletingDoc.id }),
      });
      if (!res.ok) throw new Error("Error al eliminar");
      setDocuments((prev) => prev.filter((d) => d.id !== deletingDoc.id));
      setDeletingDoc(null);
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("No se pudo eliminar el documento.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      const clientName = (clientMap[doc.rut] || "").toLowerCase();
      const matchRut = !filterRut || doc.rut.includes(filterRut);
      const matchName = !filterName || clientName.includes(filterName.toLowerCase());
      let matchDate = true;
      if (filterDateFrom || filterDateTo) {
        const docDate = doc.uploadDate ? new Date(doc.uploadDate) : null;
        if (!docDate || isNaN(docDate.getTime())) {
          matchDate = false;
        } else {
          const docDay = new Date(docDate.getFullYear(), docDate.getMonth(), docDate.getDate());
          if (filterDateFrom) {
            const from = new Date(filterDateFrom + "T00:00:00");
            if (docDay < from) matchDate = false;
          }
          if (filterDateTo) {
            const to = new Date(filterDateTo + "T00:00:00");
            if (docDay > to) matchDate = false;
          }
        }
      }
      return matchRut && matchName && matchDate;
    });
  }, [documents, clientMap, filterRut, filterName, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="w-screen min-h-screen bg-neutral-200 flex flex-col items-center">
      <NavbarPag />

      <div className="w-11/12 max-w-5xl mt-6 pb-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Documentos Subidos</h1>
          <button
            onClick={() => { setIsUploadModalOpen(true); fetchClients(); }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded shadow"
          >
            + Subir Documento
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Filtrar por RUT</label>
            <input
              type="text"
              value={filterRut}
              onChange={(e) => { setFilterRut(e.target.value); setCurrentPage(1); }}
              placeholder="Ej: 12345678-9"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Filtrar por nombre</label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => { setFilterName(e.target.value); setCurrentPage(1); }}
              placeholder="Nombre del cliente"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha desde</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha hasta</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {(filterRut || filterName || filterDateFrom || filterDateTo) && (
            <button
              onClick={() => { setFilterRut(""); setFilterName(""); setFilterDateFrom(""); setFilterDateTo(""); setCurrentPage(1); }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded text-sm"
            >
              Limpiar filtros
            </button>
          )}
          <span className="text-xs text-gray-400 self-end pb-2">
            {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filtered.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">RUT</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">Nombre Cliente</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">Archivo</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">Fecha</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((doc) => (
                      <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 text-sm font-mono">{doc.rut}</td>
                        <td className="p-3 text-sm">{clientMap[doc.rut] || "—"}</td>
                        <td className="p-3 text-sm max-w-xs truncate" title={doc.fileName}>
                          {doc.fileName}
                        </td>
                        <td className="p-3 text-sm text-gray-600">{formatDate(doc.uploadDate)}</td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDownload(doc.id)}
                              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs"
                            >
                              Descargar
                            </button>
                            <button
                              onClick={() => setDeletingDoc(doc)}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-center items-center gap-3 p-4 border-t border-gray-100">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  ← Anterior
                </button>
                <span className="text-sm font-semibold text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  Siguiente →
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 py-12">
              {documents.length === 0
                ? "No hay documentos subidos aún"
                : "No hay documentos que coincidan con los filtros"}
            </p>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Subir Documento</h2>
              <button
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setMessage("");
                  setRut("");
                  setName("");
                  setFiles([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">RUT del Cliente:</label>
                {clientsError ? (
                  <div className="bg-red-100 text-red-700 text-sm p-2 rounded mb-1">
                    Error cargando clientes.{" "}
                    <button type="button" onClick={fetchClients} className="underline font-semibold">
                      Reintentar
                    </button>
                  </div>
                ) : null}
                <select
                  value={rut}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRut(val);
                    const client = clients.find((c) => c.rut === val);
                    setName(client?.name || "");
                  }}
                  className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecciona RUT</option>
                  {clients.map((client) => (
                    <option key={`${client.rut}-${client.name}`} value={client.rut}>
                      {client.rut} - {client.name}
                    </option>
                  ))}
                </select>
              </div>

              {name && (
                <div>
                  <label className="block text-sm font-semibold mb-1">Cliente:</label>
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-2">
                    {name}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Documentos (PDF, Excel, Word):
                </label>
                <input
                  type="file"
                  accept=".pdf,.xlsx,.xls,.docx,.doc"
                  multiple
                  onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                  className="w-full border border-gray-300 p-2 rounded"
                  required
                />
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="text-sm text-green-600">✓ {f.name}</li>
                    ))}
                  </ul>
                )}
              </div>

              {message && (
                <div
                  className={`p-3 rounded text-sm ${
                    message.includes("✓")
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={isUploading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 rounded transition"
              >
                {isUploading ? "Subiendo..." : `Subir ${files.length > 1 ? `${files.length} Documentos` : "Documento"}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación de documento */}
      {deletingDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-6">
            <h2 className="text-xl font-bold mb-3">Eliminar documento</h2>
            <p className="text-sm text-gray-700 mb-5">
              Se eliminará el archivo{" "}
              <span className="font-semibold break-all">{deletingDoc.fileName}</span>.
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingDoc(null)}
                className="px-4 py-2 rounded text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteDoc}
                disabled={deleteLoading}
                className="px-4 py-2 rounded text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold"
              >
                {deleteLoading ? "Eliminando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUploadDocuments;
